// src/pages/OrganizasyonSemasiPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = '';

const DEP_RENKLER = [
  '#4F46E5','#7C3AED','#DB2777','#DC2626','#D97706',
  '#059669','#0891B2','#2563EB','#64748B','#374151',
  '#B45309','#065F46','#1E40AF','#6D28D9','#BE185D'
];

export default function OrganizasyonSemasiPage() {
  const [veri, setVeri] = useState({ departmanlar: [], pozisyonlar: [], personeller: [] });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [seciliKart, setSeciliKart] = useState(null);
  const [filtreDep, setFiltreDep] = useState('');
  const [zoom, setZoom] = useState(0.8);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [collapsed, setCollapsed] = useState({});
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const lastTouchRef = useRef(null);
  const lastPinchDistRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const getir = useCallback(async () => {
    try {
      setYukleniyor(true);
      const r = await fetch(`${API}/api/organizasyon/orgchart`, { headers });
      const d = await r.json();
      setVeri(d);
    } catch {}
    finally { setYukleniyor(false); }
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const merkezle = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;
    const cw = container.offsetWidth;
    const ch = container.offsetHeight;
    const contentW = content.scrollWidth;
    const contentH = content.scrollHeight;
    setPan({
      x: (cw - contentW * zoom) / 2,
      y: Math.max(40, (ch - contentH * zoom) / 2)
    });
  }, [zoom]);

  useEffect(() => {
    if (!yukleniyor) setTimeout(merkezle, 100);
  }, [yukleniyor, merkezle, filtreDep]);

  const depRenkMap = {};
  (veri.departmanlar || []).forEach((d, i) => {
    depRenkMap[d.id] = d.renk || DEP_RENKLER[i % DEP_RENKLER.length];
  });

  const agacOlustur = (pozlar) => {
    const map = {};
    pozlar.forEach(p => { map[p.id] = { ...p, children: [] }; });
    const kokler = [];
    pozlar.forEach(p => {
      if (p.ust_pozisyon_id && map[p.ust_pozisyon_id]) {
        map[p.ust_pozisyon_id].children.push(map[p.id]);
      } else {
        kokler.push(map[p.id]);
      }
    });
    return kokler;
  };

  const filtreUygula = () => {
    const tumPoz = veri.pozisyonlar || [];
    if (!filtreDep) return tumPoz;
    const depPozIdler = new Set(
      tumPoz.filter(p => String(p.departman_id) === String(filtreDep)).map(p => p.id)
    );
    const eklenecek = new Set(depPozIdler);
    const idMap = {};
    tumPoz.forEach(p => { idMap[p.id] = p; });
    depPozIdler.forEach(id => {
      let poz = idMap[id];
      while (poz && poz.ust_pozisyon_id) {
        eklenecek.add(poz.ust_pozisyon_id);
        poz = idMap[poz.ust_pozisyon_id];
      }
    });
    return tumPoz.filter(p => eklenecek.has(p.id));
  };

  const agac = agacOlustur(filtreUygula());

  // Mouse events
  const handleMouseDown = (e) => {
    if (e.target.closest('.org-kart')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);

  // Touch events
  const handleTouchStart = (e) => {
    if (e.target.closest('.org-kart')) return;
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      lastTouchRef.current = { x: touch.clientX - pan.x, y: touch.clientY - pan.y };
      lastPinchDistRef.current = null;
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDistRef.current = Math.sqrt(dx * dx + dy * dy);
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && lastTouchRef.current) {
      const touch = e.touches[0];
      setPan({ x: touch.clientX - lastTouchRef.current.x, y: touch.clientY - lastTouchRef.current.y });
    } else if (e.touches.length === 2 && lastPinchDistRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const delta = (dist - lastPinchDistRef.current) * 0.005;
      setZoom(z => Math.min(2, Math.max(0.2, z + delta)));
      lastPinchDistRef.current = dist;
    }
  };

  const handleTouchEnd = () => {
    lastTouchRef.current = null;
    lastPinchDistRef.current = null;
  };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    setZoom(z => Math.min(2, Math.max(0.2, z + delta)));
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener('wheel', handleWheel, { passive: false });
      el.addEventListener('touchmove', handleTouchMove, { passive: false });
    }
    return () => {
      if (el) {
        el.removeEventListener('wheel', handleWheel);
        el.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [handleWheel, pan, dragging]);

  const toggleCollapse = (e, id) => {
    e.stopPropagation();
    setCollapsed(c => ({ ...c, [id]: !c[id] }));
  };

  const pozPersonel = (pozId) => (veri.personeller || []).filter(p => p.pozisyon_id === pozId);

  const OrgKart = ({ poz }) => {
    const renk = depRenkMap[poz.departman_id] || '#4F46E5';
    const personeller = pozPersonel(poz.id);
    const bos = personeller.length === 0;
    const secili = seciliKart?.id === poz.id;
    const kapali = collapsed[poz.id];
    const cocukVar = poz.children && poz.children.length > 0;
    const depFiltreli = filtreDep && String(poz.departman_id) !== String(filtreDep);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          className="org-kart"
          onClick={() => setSeciliKart(secili ? null : { ...poz, personeller, renk })}
          style={{
            background: depFiltreli ? '#F9FAFB' : bos ? '#FAFAFA' : '#fff',
            border: `1.5px solid ${secili ? renk : depFiltreli ? '#E5E7EB' : renk + '50'}`,
            borderTop: `3px solid ${depFiltreli ? '#E5E7EB' : renk}`,
            borderRadius: '10px',
            padding: '10px 12px',
            width: '160px',
            cursor: 'pointer',
            boxShadow: secili ? `0 4px 20px ${renk}30` : '0 1px 4px rgba(0,0,0,0.06)',
            transition: 'all 0.15s',
            position: 'relative',
            opacity: depFiltreli ? 0.5 : 1,
          }}
        >
          {bos && !depFiltreli && (
            <div style={{
              position: 'absolute', top: '-8px', right: '6px',
              background: '#FEF3C7', color: '#92400E',
              fontSize: '9px', fontWeight: 700,
              padding: '1px 6px', borderRadius: '99px',
              border: '1px solid #F59E0B'
            }}>BOS</div>
          )}
          <div style={{ fontSize: '9px', fontWeight: 700, color: depFiltreli ? '#9CA3AF' : renk, marginBottom: '4px', textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {poz.departman_ad}
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: depFiltreli ? '#9CA3AF' : '#111827', lineHeight: 1.3, marginBottom: '6px', minHeight: '28px' }}>
            {poz.ad}
          </div>
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
            {personeller.slice(0, 3).map(p => (
              <div key={p.id} style={{ width: '24px', height: '24px', borderRadius: '50%', background: renk, color: '#fff', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #fff', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>{p.ad[0]}{p.soyad[0]}</div>
            ))}
            {personeller.length > 3 && (
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#F3F4F6', color: '#6B7280', fontSize: '9px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+{personeller.length - 3}</div>
            )}
            {bos && (
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#F3F4F6', border: '1.5px dashed #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#9CA3AF' }}>+</div>
            )}
          </div>
        </div>

        {cocukVar && (
          <button onClick={(e) => toggleCollapse(e, poz.id)}
            style={{ width: '20px', height: '20px', borderRadius: '50%', background: renk, color: '#fff', border: 'none', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px 0', zIndex: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}>
            {kapali ? '+' : '-'}
          </button>
        )}

        {cocukVar && !kapali && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: '1.5px', height: '16px', background: '#D1D5DB' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative' }}>
              {poz.children.length > 1 && (
                <div style={{ position: 'absolute', top: 0, left: `${(160 + 24) / 2}px`, right: `${(160 + 24) / 2}px`, height: '1.5px', background: '#D1D5DB' }} />
              )}
              {poz.children.map((child) => (
                <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 12px' }}>
                  <div style={{ width: '1.5px', height: '16px', background: '#D1D5DB' }} />
                  <OrgKart poz={child} />
                </div>
              ))}
            </div>
          </div>
        )}

        {cocukVar && kapali && (
          <div style={{ background: '#F3F4F6', border: '1px solid #E5E7EB', borderRadius: '99px', padding: '2px 10px', fontSize: '11px', color: '#6B7280', fontWeight: 600, marginTop: '2px', cursor: 'pointer' }}
            onClick={(e) => toggleCollapse(e, poz.id)}>
            {poz.children.length} pozisyon gizli
          </div>
        )}
      </div>
    );
  };

  const tumPoz = veri.pozisyonlar || [];
  const dolular = tumPoz.filter(p => (veri.personeller || []).some(pr => pr.pozisyon_id === p.id));

  return (
    <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <div style={{ padding: '12px 20px', borderBottom: '1px solid #E5E7EB', background: '#fff', display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>Organizasyon Semasi</h1>
        <div style={{ flex: 1 }} />
        <select value={filtreDep} onChange={e => { setFiltreDep(e.target.value); setCollapsed({}); }}
          style={{ padding: '7px 10px', border: '1.5px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', minWidth: '180px' }}>
          <option value="">Tum Departmanlar</option>
          {(veri.departmanlar || []).map(d => <option key={d.id} value={d.id}>{d.ad}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#F3F4F6', padding: '4px 8px', borderRadius: '8px' }}>
          <button onClick={() => setZoom(z => Math.max(0.2, z - 0.1))} style={{ width: '26px', height: '26px', border: 'none', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '16px' }}>-</button>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151', minWidth: '38px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={{ width: '26px', height: '26px', border: 'none', background: '#fff', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '16px' }}>+</button>
        </div>
        <button onClick={() => { setZoom(0.8); setTimeout(merkezle, 50); }} style={{ padding: '7px 12px', border: '1.5px solid #E5E7EB', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Sifirla</button>
        <button onClick={() => setCollapsed({})} style={{ padding: '7px 12px', border: '1.5px solid #E5E7EB', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Tumunu Ac</button>
        <button onClick={getir} style={{ padding: '7px 12px', border: 'none', borderRadius: '8px', background: '#4F46E5', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Yenile</button>
      </div>

      <div style={{ padding: '8px 20px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', display: 'flex', gap: '20px', flexShrink: 0, flexWrap: 'wrap' }}>
        {[
          { label: 'Departman', value: (veri.departmanlar || []).length, renk: '#4F46E5' },
          { label: 'Pozisyon', value: tumPoz.length, renk: '#059669' },
          { label: 'Dolu', value: dolular.length, renk: '#2563EB' },
          { label: 'Bos', value: tumPoz.length - dolular.length, renk: '#D97706' },
          { label: 'Personel', value: (veri.personeller || []).length, renk: '#7C3AED' },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '16px', fontWeight: 700, color: item.renk }}>{item.value}</span>
            <span style={{ fontSize: '12px', color: '#6B7280' }}>{item.label}</span>
          </div>
        ))}
      </div>

      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          flex: 1, overflow: 'hidden', position: 'relative',
          background: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        {yukleniyor ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: '16px' }}>Yukleniyor...</div>
        ) : agac.length === 0 ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '48px' }}>🏢</div>
            <p style={{ color: '#6B7280', fontWeight: 600 }}>Pozisyon bulunamadi</p>
          </div>
        ) : (
          <div style={{ position: 'absolute', left: 0, top: 0, transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
            <div ref={contentRef} style={{ padding: '40px', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
              {agac.map(kok => <OrgKart key={kok.id} poz={kok} />)}
            </div>
          </div>
        )}
      </div>

      {seciliKart && (
        <div style={{ position: 'fixed', right: '20px', top: '50%', transform: 'translateY(-50%)', width: '260px', background: '#fff', borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `2px solid ${seciliKart.renk}`, overflow: 'hidden', zIndex: 100 }}>
          <div style={{ background: seciliKart.renk, padding: '14px 16px', color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '10px', opacity: 0.8, marginBottom: '3px', textTransform: 'uppercase' }}>{seciliKart.departman_ad}</div>
                <div style={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.3 }}>{seciliKart.ad}</div>
              </div>
              <button onClick={() => setSeciliKart(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '6px', color: '#fff', width: '26px', height: '26px', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>X</button>
            </div>
          </div>
          <div style={{ padding: '14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: seciliKart.renk }}>{seciliKart.personeller?.length || 0}</div>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>Personel</div>
              </div>
              <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: '#374151' }}>L{seciliKart.seviye}</div>
                <div style={{ fontSize: '11px', color: '#6B7280' }}>Seviye</div>
              </div>
            </div>
            {seciliKart.personeller && seciliKart.personeller.length > 0 ? (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', marginBottom: '6px' }}>Personel</div>
                {seciliKart.personeller.map(p => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px', background: '#F9FAFB', borderRadius: '7px', marginBottom: '5px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: seciliKart.renk, color: '#fff', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{p.ad[0]}{p.soyad[0]}</div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{p.ad} {p.soyad}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>{p.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '14px', background: '#FEF3C7', borderRadius: '8px' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>⚠️</div>
                <div style={{ fontSize: '12px', color: '#92400E', fontWeight: 600 }}>Bu pozisyon bos</div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ padding: '8px 20px', borderTop: '1px solid #F3F4F6', background: '#fff', display: 'flex', gap: '10px', flexWrap: 'wrap', flexShrink: 0 }}>
        <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 600, alignSelf: 'center' }}>Departmanlar:</span>
        {(veri.departmanlar || []).map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', padding: '3px 8px', borderRadius: '99px', background: filtreDep === String(d.id) ? (d.renk || DEP_RENKLER[i % DEP_RENKLER.length]) + '20' : 'transparent', border: filtreDep === String(d.id) ? `1px solid ${d.renk || DEP_RENKLER[i % DEP_RENKLER.length]}` : '1px solid transparent' }}
            onClick={() => { setFiltreDep(filtreDep === String(d.id) ? '' : String(d.id)); setCollapsed({}); }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: d.renk || DEP_RENKLER[i % DEP_RENKLER.length], flexShrink: 0 }} />
            <span style={{ fontSize: '11px', color: '#374151', fontWeight: filtreDep === String(d.id) ? 700 : 400 }}>{d.ad}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
